/**
 * @file ViewSystemGroupForm.js
 * @description Displays the details of a system population group in read-only mode.
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  fetchPopulationGroupByID,
  fetchItems,
  fetchOperators,
} from "../../services/populationGroupService";
import {
  fetchLocations,
  fetchDepartments,
  fetchDivisions,
  fetchRegions,
  fetchPayGroups,
  fetchBusinessUnits
} from "../../services/orgLevelService";
import {
  fetchEmploymentStatuses,
  fetchTiers,
  queryPlanTypes,
} from "../../services/systemService";
import { searchIndividuals } from "../../services/individualService";
import styles from "./PopulationGroup.module.scss";
import Spinner from "../ui/spinners/Spinner";
import Toast from "../ui/toasts/Toast";
import Tooltip from "../ui/tooltips/Tooltip";
import { getText } from '../../i18n';

const ViewSystemGroupForm = ({ groupId, onReturnToList }) => {
  const [group, setGroup] = useState(null);
  const [items, setItems] = useState([]);
  const [operators, setOperators] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [fulltimeOrParttime, setFulltimeOrParttime] = useState([{ id: 1, name: "Full Time", code: "FT" }, { id: 2, name: "Part Time", code: "PT" }]);
  const [employeeIDs, setEmployeeIDs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [payGroups, setPayGroups] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        setIsLoading(true);
        const [
          groupData,
          itemsData,
          operatorsData,
          locationsData,
          departmentsData,
          divisionsData,
          statusesData,
          employeeIDsData,
          tiersData,
          planTypesData,
          regionsData,
          payGroupsData,
          businessUnitsData
        ] = await Promise.all([
          fetchPopulationGroupByID(groupId),
          fetchItems(),
          fetchOperators(),
          fetchLocations(),
          fetchDepartments(),
          fetchDivisions(),
          fetchEmploymentStatuses(),
          searchIndividuals(),
          fetchTiers(),
          queryPlanTypes(),
          fetchRegions(),
          fetchPayGroups(),
          fetchBusinessUnits()
        ]);

        setGroup(groupData);
        setItems(itemsData);
        setOperators(operatorsData);
        setLocations(locationsData);
        setDepartments(departmentsData);
        setDivisions(divisionsData);
        setStatuses(statusesData);
        setEmployeeIDs(employeeIDsData.individuals.map(individual => ({
          ...individual,
          code: individual.uniqueId,
          name: `${individual.firstName} ${individual.lastName} (${individual.uniqueId})`
        })));
        setTiers(tiersData);
        setPlanTypes(planTypesData);
        setRegions(regionsData);
        setPayGroups(payGroupsData);
        setBusinessUnits(businessUnitsData);
      } catch (error) {
        console.error("Error fetching group data:", error);
        Toast.error(getText('msg_e6e0fa2a76e8'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroupData();
  }, [groupId]);

  const getAttributeData = (itemId) => {
    switch (itemId) {
      case 1: return locations;
      case 2: return departments;
      case 3: return divisions;
      case 7: return tiers;
      case 5: return planTypes;
      case 10: return statuses;
      case 11: return fulltimeOrParttime;
      case 12: return employeeIDs;
      case 39: return regions;
      case 40: return businessUnits;
      case 41: return payGroups;
      default: return [];
    }
  };

  const renderValue = (segment) => {
    const attributeData = getAttributeData(segment.itemId);
    if (segment.operatorId === 8 || segment.operatorId === 9) {
      // For multiple values
      const values = segment.value.split('~');
      return values.map(value => {
        const item = attributeData.find(attr => attr.code === value);
        return item ? item.name : value;
      }).join(', ');
    } else {
      // For single value
      const item = attributeData.find(attr => attr.code === segment.value);
      return item ? item.name : segment.value;
    }
  };

  if (isLoading) {
    return <Spinner width={50} className={styles.loadingSpinner} />;
  }

  if (!group) {
    return <div>{getText('msg_1daaec0f4ce6')}</div>;
  }

  return (
    <div className={`card ${styles.ViewSystemGroupForm}`}>
      <div className="card-body px-0 py-4">
        <div className="d-flex justify-content-between align-items-center">
          <p className="page-title float-start ms-4">{getText('msg_75903d68cd32')}</p>
          <div className="float-end align-items-center me-4">
            <p
              type="button"
              onClick={onReturnToList}
              className="ing-text-buttons d-inline ms-3"
            >
              <i className="bi bi-x me-2" />
              {getText('msg_bbfa773e5a63')}
            </p>
          </div>
        </div>
        <div className={`form-container p-3 ${styles.groupForm}`}>
          <div className={`form-group mb-3 col-12 col-xs-12 col-md-6 col-lg-4`}>
            <label htmlFor="groupName">{getText('msg_709a23220f2c')}</label>
            <input
              id="groupName"
              type="text"
              className={`form-control ${styles.nameClassName}`}
              value={group.name}
              readOnly
              disabled
            />
          </div>

          <div className="mb-3">
            <div className={`gap-2 mb-2 d-lg-flex justify-content-md-start d-sm-none ${styles.segmentRow}`}>
              <label className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}>{getText('msg_ecdda59aea5e')}</label>
              <label className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}>{getText('msg_d0e687b079fb')}</label>
              <label className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}>{getText('msg_8dce170de238')}</label>
            </div>

            {group.segments.map((segment, index) => (
              <div key={index} className={`col-12 gap-2 mb-2 d-lg-flex justify-content-md-start ${styles.segmentRow}`}>
                <div className={`me-md-2 mb-2 ${styles.segmentInputItem}`}>
                  <input
                    type="text"
                    className="form-control"
                    value={items.find(item => item.id === segment.itemId)?.name || ''}
                    readOnly
                    disabled
                  />
                </div>
                <div className={`me-md-2 mb-2 ${styles.segmentInputItem}`}>
                  <input
                    type="text"
                    className="form-control"
                    value={operators.find(op => op.id === segment.operatorId)?.name || ''}
                    readOnly
                    disabled
                  />
                </div>
                <div className={`me-md-2 mb-2 ${styles.segmentInputItem}`}>
                  <div style={{ position: 'relative' }}>
                    <Tooltip
                      html={renderValue(segment)}
                      placement="bottom"
                    >
                      <input
                        type="text"
                        className="form-control"
                        value={renderValue(segment)}
                        readOnly
                        disabled
                        style={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          paddingRight: '25px'
                        }}
                      />

                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

ViewSystemGroupForm.propTypes = {
  groupId: PropTypes.number.isRequired,
  onReturnToList: PropTypes.func.isRequired,
};

export default ViewSystemGroupForm;
